import { IGroupListItemDef } from "@components/configurationList";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy } from "@components/smart/smartTable";
import BindingContext from "@odata/BindingContext";
import { ElectronicSubmissionTypeCode } from "@odata/GeneratedEnums";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { FieldType, Sort } from "../../../enums";
import {
    vatPeriodParameterOnBeforeLoadCallback,
    vatStatementPeriodParamDef
} from "../customFilterComponents/VatPeriodParameter";
import {
    IReportTableDefinition,
    NumberAggregationFunction,
    ReportConfigGroup,
    TGetReportConfigListItemOverride,
    TimeAggregationFunction,
    TReportColumnOverrides,
    TReportConfigListItemOverride
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";
import { getVatReportColumnOverrides, VatReportProps } from "../VatReport.utils";

export const OSS_STATEMENT_REPORT_PATH = "VatOssStatementReport";

export const VatOssStatementReportProps = {
    inTransactionCurrency: BindingContext.localContext("InTransactionCurrency")
};

export const getDefinition = (context: IAppContext): IReportTableDefinition => {
    const tableId = ReportId.VatStatement;
    const title = i18next.t("Reporting:VatOssStatement.Title");
    const path = OSS_STATEMENT_REPORT_PATH;
    const initialSortBy = [{ id: "Document_VatStatementSection", sort: Sort.Asc }];
    const parameters: string[] = [
        VatReportProps.vatStatementPeriod,
        VatOssStatementReportProps.inTransactionCurrency
    ];

    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                VatReportProps.vatStatementPeriod,
                VatOssStatementReportProps.inTransactionCurrency
            ],
            filterDefinition: {
                [VatReportProps.vatStatementPeriod]: vatStatementPeriodParamDef(),
                [VatOssStatementReportProps.inTransactionCurrency]: {
                    type: FieldType.ComboBox,
                    defaultValue: false,
                    label: i18next.t("Reporting:VatOssStatement.ShowTransactionCurrency"),
                    fieldSettings: {
                        items: [
                            {
                                label: i18next.t("Reporting:VatOssStatement.InTransactionCurrency"),
                                id: true
                            },
                            {
                                label: i18next.t("Reporting:VatOssStatement.InEur"),
                                id: false
                            }
                        ]
                    }
                }
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {}
        }
    ];

    const onBeforeLoad = async (storage: ReportStorage) => {
        await vatPeriodParameterOnBeforeLoadCallback(storage, ElectronicSubmissionTypeCode.VatOSSStatement);
    };


    const configListItemsOverride: TGetReportConfigListItemOverride = (item: IGroupListItemDef): TReportConfigListItemOverride => {
        switch (true) {
            case item.id === "Document_VatStatementSection":
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    group: ReportConfigGroup.Groups
                };
        }

        return item;
    };

    const columnOverrides: TReportColumnOverrides = {
        ...getVatReportColumnOverrides()
    };

    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": true,
        "Groups": [
            {
                "ColumnAlias": "BusinessPartner_CountryCode"
            }
        ],
        "Columns": [
            {
                "ColumnAlias": "BusinessPartner_TaxNumber"
            },
            {
                "ColumnAlias": "BusinessPartner_Name"
            },
            {
                "ColumnAlias": "Document_NumberOurs"
            },
            {
                "ColumnAlias": "Document_DateTaxableSupply",
                "AggregationFunction": TimeAggregationFunction.Month
            }
        ],
        "Aggregations": [
            {
                "ColumnAlias": "Document_StdAmountNet",
                "AggregationFunction": NumberAggregationFunction.Sum
            },
            {
                "ColumnAlias": "Document_StdAmountVat",
                "AggregationFunction": NumberAggregationFunction.Sum
            },
            {
                "ColumnAlias": "Document_RdcAmountNet",
                "AggregationFunction": NumberAggregationFunction.Sum
            },
            {
                "ColumnAlias": "Document_RdcAmountVat",
                "AggregationFunction": NumberAggregationFunction.Sum
            },
            {
                "ColumnAlias": "Document_NoVatAmountNet",
                "AggregationFunction": NumberAggregationFunction.Sum
            },
            {
                "ColumnAlias": "Document_TotalAmount",
                "AggregationFunction": NumberAggregationFunction.Sum
            }
        ]
    };


    return {
        title, path, id: tableId, initialSortBy, filterBarDef, parameters, columnOverrides,
        onBeforeLoad, defaultReportHierarchy, configListItemsOverride
    };
};
